import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';
import CardFooter from '../md/components/Card/CardFooter.jsx';

import loginPageStyle from '../assets/jss/crm/views/loginPageStyle.jsx';

import logo from '../assets/img/allevaLogo02.png';

import { setUserInfo } from '../store/slices/userInfoSlice';
import { pushLogin } from '../store/slices/loginsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';
import { markUserAsLoggedOutDueToIdleTimeout } from '../store/slices/miscSlice';

import { createStdRequest, apiFetch, showAlert } from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import CrmTextField from '../components/CrmTextField';
import AlertModal from '../components/AlertModal';
import KSpinner from '../components/KSpinner';

const localStyles = (theme) => ({
  ...loginPageStyle(theme),
  textFieldHolder: {
    marginTop: '20px',
  },
  signInButton: {
    width: '100% !important',
    padding: '12px 0',
    borderRadius: '10px',
  },
  signInSpan: {
    color: 'white',
  },
});

class LoginPage extends React.Component {
  emailPattern = /[a-zA-Z0-9+_]+@[a-zA-Z0-9]+.[a-zA-Z][a-zA-Z]+/;
  visibleErrorStyle = { color: 'red', visibility: 'visible', textAlign: 'center' };
  hiddenErrorStyle = { visibility: 'hidden' };

  constructor(props) {
    super(props);

    // we use cardAnimaton to make the card to appear after the page has been rendered
    this.state = {
      cardAnimaton: 'cardHidden',
      email: '',
      password: '',
      toHome: false,
      errorModalTitle: 'Login Failed',
    };
  }

  componentDidMount() {
    markUserAsLoggedOutDueToIdleTimeout(false); // this may be obsolete since we added the following two lines

    const includingLoginInfo = true;
    DA.clearStore(includingLoginInfo);

    // we add a hidden class to the card and after 700 ms we delete it and the transition appears
    this.timeOutFunction = setTimeout(
      function () {
        this.setState({ cardAnimaton: '' });
      }.bind(this),
      700
    );
  }

  componentWillUnmount() {
    clearTimeout(this.timeOutFunction);
    this.timeOutFunction = null;
  }

  validateEmail(email) {
    if (email.length < 6) {
      return false;
    }
    return this.emailPattern.test(email);
  }

  validatePassword(password) {
    return password.length >= 8;
  }

  validateForm() {
    return this.validateEmail(this.state.email) && this.validatePassword(this.state.password);
  }

  handleTextChange = (event) => {
    this.setState({
      [event.target.id]: event.target.value.trim(),
    });
  };

  handleKeyUp(event) {
    // Number 13 is the "Enter" key on the keyboard
    if (event.keyCode === 13) {
      // Cancel the default action, if needed
      event.preventDefault();
      this.handleSubmit(event);
    }
  }

  handleSubmit = (event) => {
    event.preventDefault();
    const credentialsObj = {
      email: this.state.email,
      password: this.state.password,
    };
    const uri = '/api/account/logInTest';
    const request = createStdRequest(uri, 'POST', credentialsObj);
    const actionOnGoodResponse = (json) => {
      clearTimeout(loginTimeout);
      const { success, userId, personId, email, phone, role, prefName, lastName, firmId, errorCode } = json;
      if (success) {
        this.setState({ loading: true });
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        this.props.setUserInfo(userInfo);
        const { password } = credentialsObj; // already have email
        const loginObj = { loginType: 'textual', email, password };
        this.props.pushLogin(loginObj);

        if (role === 'User' || role === 'Admin') {
          const completion = () => {
            this.setState({ loading: false, toHome: true, whichHome: '/u/home' });
          };
          ReduxLoader.loadFirmInfo(firmId, this, completion);
        } else if (role === 'SuperAdmin' || role === 'AllevaRep') {
          const firmInfo = { firmId: 0, firmName: '' };
          ReduxLoader.setFirmInfoAndNotifyWebSocket(firmInfo);
          ReduxLoader.quasiLoadStateForNoFirm();
          const whichHome = role === 'SuperAdmin' ? '/sa/home' : '/ar/home';
          this.setState({ loading: false, toHome: true, whichHome });
        } else {
          this.setState({ toHome: true, whichHome: '/v/home' });
          showAlert(this, "You appear to be able to log in, but have no 'role.' Our DB must be messed up.");
        }
      } else {
        // Possible problems, by error code:
        //   1) No such user name (email)
        //   2) Good user name, but bad password
        //   3) Requires 2-factor auth
        //   4) User is locked out
        //   5) Account has been deactivated
        switch (errorCode) {
          case 1:
            showAlert(this, 'We have no account with that user name (Email)');
            break;
          case 2:
            showAlert(this, 'The provided password does not match the specified user (Email)');
            break;
          case 3:
            showAlert(this, 'This account requires 2-factor authentication');
            break;
          case 4:
            showAlert(this, 'This user account is locked out');
            break;
          case 5:
            showAlert(this, 'This account has been deactivated');
            break;
          default:
            showAlert(this, 'Unknown login error. Error code: ' + errorCode);
            break;
        }
      }
    };
    const loginTimeout = setTimeout(() => this.notifyOfLongDelay(), 5000);
    apiFetch(this, request, actionOnGoodResponse);
  };

  notifyOfLongDelay() {
    showAlert(this, 'The login process is taking unexpectedly long, but we still expect it to complete.');
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.toHome) {
      return <Redirect to="/homeDispatcher" push />;
    }

    if (this.state.loading) {
      return <KSpinner />;
    }

    const { classes } = this.props;
    return (
      <div>
        <AlertModal renderer={this} show={this.state.shouldShowAlertModal} text={this.state.alertModalText} />

        <div className={classes.container}>
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Card className={classes[this.state.cardAnimaton]}>
                <CardBody>
                  <div className={classes.logoDiv}>
                    <img src={logo} alt="logo" className={classes.logo} />
                  </div>
                  <div>
                    <CrmTextField
                      label="Email"
                      id="email"
                      fullWidth
                      showingAutoFill
                      value={this.state.email}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Password"
                      id="password"
                      password
                      fullWidth
                      showingAutoFill
                      value={this.state.password}
                      onChange={(event) => this.handleTextChange(event)}
                      onKeyUp={(event) => this.handleKeyUp(event)}
                    />
                  </div>
                </CardBody>
                <CardFooter className={classes.justifyContentCenter}>
                  <Button
                    className={classes.signInButton}
                    color="primary"
                    variant="contained"
                    onClick={(event) => this.handleSubmit(event)}
                  >
                    <span className={classes.signInSpan}>Sign In</span>
                  </Button>
                </CardFooter>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }
}

LoginPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

// export default withStyles(localStyles)(LoginPage);

const actionProducers = { setUserInfo, pushLogin, setApiResponseInfo };

export default connect(null, actionProducers)(withStyles(localStyles)(LoginPage));
